/**
 * Pilot feedback on the customer or on one line: "Right" or "Wrong".
 *
 * "Right" stays the one-click confirmation (`POST /cases/{id}/feedback`, the quick
 * verdict the pilot metrics count). "Wrong" opens the "Give feedback" dialog prefilled
 * with "Wrong result", the topic, the case and the line, so the person can say what is
 * right (docs/feedback.md section 3). Outside the app shell (no dialog provider) "Wrong"
 * saves the bare verdict as before.
 *
 * Stored in the app database only; the managers of the area see it in the feedback
 * triage and on the dashboard. The author's newest verdict is shown above the control.
 * Nothing is sent to anybody.
 */

import { Icon, ThumbsDown, ThumbsUp } from "@norican/icons";
import { Button, Collapsible, Toggle, ToggleGroup, Typography } from "@norican/react";
import { useState } from "react";

import { useGiveFeedback } from "@/features/feedback";
import {
  type CaseFeedback,
  feedbackFailureText,
  type FeedbackTopic,
  type FeedbackVerdict,
  latestFeedback,
  useCaseFeedback,
  useMe,
  useRecordFeedback,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { formatDayTime } from "@/lib/work-labels";

const TOPIC_WORDS: Record<FeedbackTopic, string> = {
  customer: "the customer",
  item: "the item",
  quantity: "the quantity",
  price: "the price",
  other: "this case",
};

/** "You said Wrong on the item, 08 Oct 2026, 14:05." */
export function feedbackSentence(row: Pick<CaseFeedback, "verdict" | "topic" | "at">): string {
  const verdict = row.verdict === "right" ? "Right" : "Wrong";
  const topic = Object.hasOwn(TOPIC_WORDS, row.topic) ? TOPIC_WORDS[row.topic] : "this case";
  const when = formatDayTime(row.at);
  return `You said ${verdict} on ${topic}${when ? `, ${when}` : ""}.`;
}

export interface FeedbackControlProps {
  caseId: string;
  /** Position of the line in the stored draft; null for the customer of the case. */
  lineIndex: number | null;
  /** What can be judged; the first entry is preselected. A single entry shows no picker. */
  topics?: readonly { value: FeedbackTopic; label: string }[];
  className?: string;
}

const CUSTOMER_TOPIC: readonly { value: FeedbackTopic; label: string }[] = [
  { value: "customer", label: "Customer" },
];

/** Right saves at once; Wrong opens the feedback dialog. Closed until opened. */
export function FeedbackControl({
  caseId,
  lineIndex,
  topics = CUSTOMER_TOPIC,
  className,
}: FeedbackControlProps) {
  const { data: me } = useMe();
  const rows = useCaseFeedback(caseId);
  const record = useRecordFeedback(caseId);
  const give = useGiveFeedback();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState<FeedbackTopic>(topics[0]?.value ?? "other");

  const latest = latestFeedback(rows.data ?? [], me?.email, lineIndex);
  const error = record.error ? feedbackFailureText(record.error) : null;

  const quick = (verdict: FeedbackVerdict) => {
    logger.debug("[feedback] quick verdict", { case: caseId, lineIndex, topic, verdict });
    record.mutate({ line_index: lineIndex, topic, verdict }, { onSuccess: () => setOpen(false) });
  };

  const wrong = () => {
    if (!give) {
      quick("wrong");
      return;
    }
    record.reset();
    give.open({
      surface: lineIndex === null ? "customer" : "line",
      caseId,
      lineIndex,
      kind: "model",
      topic,
      onSaved: () => setOpen(false),
    });
  };

  return (
    <div className={className}>
      {latest && (
        <Typography variant="caption" tone="muted" className="block break-words">
          {feedbackSentence(latest)}
        </Typography>
      )}
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
          {open
            ? "Hide feedback"
            : latest
              ? "Change your feedback"
              : "Was this right? Give feedback"}
        </Collapsible.Trigger>
        <Collapsible.Panel>
          <div className="mt-2 flex flex-col gap-2">
            {topics.length > 1 && (
              <ToggleGroup.Root
                size="sm"
                value={[topic]}
                onValueChange={(value) => {
                  const picked = (value as string[])[0];
                  if (picked) {
                    setTopic(picked as FeedbackTopic);
                  }
                }}
                aria-label="What the feedback is about"
              >
                {topics.map((option) => (
                  <Toggle key={option.value} value={option.value} size="sm">
                    {option.label}
                  </Toggle>
                ))}
              </ToggleGroup.Root>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                loading={record.isPending}
                startIcon={<Icon icon={ThumbsUp} size="xs" />}
                onClick={() => quick("right")}
              >
                Right
              </Button>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={record.isPending}
                startIcon={<Icon icon={ThumbsDown} size="xs" />}
                onClick={wrong}
              >
                Wrong
              </Button>
            </div>
            <Typography variant="caption" tone="subtle">
              I2Q saves Right at once. Wrong asks what is right, so the build team can use it.
            </Typography>
            {error && (
              <Typography variant="caption" className="text-danger-fg" role="alert">
                {error}
              </Typography>
            )}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  );
}
